import React from "react";
import produce from "immer";
import environment, { electronOnly } from "src/utils/environment";
import decodeOgg from "src/browser/decodeOgg";
import AppStatus from "src/constants/AppStatus";
import Presence from "src/constants/presence";
import RPC from "src/modules/rpc";
import {
  RPCResponses,
  RPCRequests,
  Message
} from "src/modules/communication-protobuf";
import debug from "src/modules/debug-logger";
import appDebugger from "src/modules/debug/AppDebugger";
import MicSensitivity from "src/modules/station/enums/MicSensitivity";
import createActivityEmitter from "src/modules/audio-processing/createActivityEmitter";
import commandFiles from "src/modules/command/command-files";
import BabyStationRtcManager from "src/modules/baby-station/lib/BabyStationRtcManager";
import createPushNotificator from "src/modules/baby-station/lib/createPushNotificator";
import ActivityLogManager from "src/modules/activity-log/lib/ActivityLogManager";
import MotionDetection from "src/modules/motion-detection/MotionDetection";
import { Activities } from "src/modules/activity-log/activity";
import Scheduler from "src/modules/scheduler";
import { store } from "src/store/store";
import { lastRoleChanged } from "src/duck/actions";

import createBatteryStatusPayload from "./createBatteryStatusPayload";
import NoAudioDialog from "../components/NoAudioDialog";
import StartScheduledMonitoringDialog from "../components/StartScheduledMonitoringDialog";
import StopMonitoringDialog from "../components/StopMonitoringDialog";
import Monitor from "../scenes/Monitor";
import BabyStationContext from "./Context";

import { v4 as uuid4 } from "uuid";
import monitoringRestorer from "src/modules/monitoring/monitoringRestorer";
import {
  createDeviceHandler,
  getCameraAndMicrophoneInfo
} from "./media-devices";
const remote = window.require("@electron/remote");
const log = window.require("electron-log");

const LOW_BATTERY_NOTIFICATION_LEVEL = 15;

const initialState = {
  activity: Activities.SLEEPING,
  activityChangeTime: null,
  error: false,
  monitoringId: null,
  hasCameraPermission: true,
  status: "starting", // |starting, stopping, started|
  // parent = { jid, versionInfo, supportedFeatures, commandPlayer, requestedActivityLog, monitoringModeType }
  parents: {},
  localVideoStream: null,
  videoReceivingFrom: null,
  monitoringStartTime: null,
  activityEmitter: null,
  cameraList: null,
  selectedCameraId: null,
  selectedMicrophoneId: null,
  indicateMotion: false,
  canDetectMotion: true,
  renderStopMonitoringDialog: false,
  renderScheduledMonitoringDialog: false
};
export default class BabyStation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ...initialState,
      switchAvatar: this.handleAvatarChange,
      handleMicSensitivityChange: this.handleMicSensitivityChange,
      changeMotionDetectionSensitivity: this.changeMotionDetectionSensitivity
    };
  }

  batteryStatus = null;
  rpc = null;
  rtcManager = null;
  videoTrack = null;
  mediaRecorder = null;
  motionDetection = null;
  scheduler = null;
  commandPlayers = {};
  pushNotificator = createPushNotificator({
    messenger: this.props.messenger,
    getPeers: () => this.state.parents,
    getMonitoringId: () => this.state.monitoringId
  });
  activityLogPushNotificator = null;
  activityLogManager = null;
  deviceHandlerUnsubscribe = null;

  getNumberOfParents = () => Object.keys(this.state.parents).length;
  updateParent = (jid, update) =>
    this.setState(
      produce(({ parents }) => {
        parents[jid] = {
          ...parents[jid],
          ...update
        };
      })
    );

  componentDidMount = async () => {
    const {
      messenger,
      defaultAvatarId,
      micSensitivity,
      micSensitivityCorrection,
      app
    } = this.props;
    store.dispatch(lastRoleChanged("baby"));
    this.scheduler = new Scheduler(messenger);
    this.motionDetection = new MotionDetection({
      getEventManager: () => this.activityLogManager.eventManager,
      messenger: this.props.messenger,
      getPeers: () => this.state.parents,
      getActivityEmitter: () => this.state.activityEmitter,
      handleDetectionAvailabilityChange: this.handleDetectionAvailabilityChange,
      handleMotionIndication: this.handleMotionIndication,
      notifyAboutMotionAvailabilityChange:
        this.notifyAboutMotionAvailabilityChange
    });
    this.rtcManager = new BabyStationRtcManager({
      messenger,
      onRecreateConnection: (parentJid) => {
        if (this.state.videoReceivingFrom === parentJid) {
          this.setState({ videoReceivingFrom: null, remoteVideoStream: null });
        }
      }
    });

    appDebugger.babyRtcManager = this.rtcManager;
    if (appDebugger.state === "enabled") {
      appDebugger.startBabyDebug();
    }

    this.rtcManager.on("media-track", this.onTrack);

    let hasCameraPermission = false;
    try {
      hasCameraPermission = await app.permissions.resolveMediaPermission(
        "camera"
      );
    } catch (error) {
      log.error(error.message || error);
      hasCameraPermission = false;
    }

    if (hasCameraPermission) {
      try {
        const { cameraId, cameraList, microphoneId } =
          await getCameraAndMicrophoneInfo();

        this.setState(
          {
            cameraList,
            selectedCameraId: cameraId,
            selectedMicrophoneId: microphoneId
          },
          () =>
            (this.deviceHandlerUnsubscribe = createDeviceHandler({
              getCameraId: () => this.state.selectedCameraId,
              getMicrophoneId: () => this.state.selectedMicrophoneId,
              getCameraList: () => this.state.cameraList
            }))
        );
      } catch (err) {
        log.error(err.message || err);
        this.props.router.navigate("/dashboard");
      }
    }

    try {
      await this.rtcManager.initialize({
        hasCameraPermission
      });
      if (this.unMounted) {
        this.rtcManager && this.rtcManager.destroy();
        return;
      }

      this.noiseMeter = this.rtcManager.opusRecorder.noiseMeter;
      this.noiseMeter.setSensitivity(
        MicSensitivity[micSensitivity].value,
        micSensitivityCorrection
      );
    } catch (error) {
      log.error(error.message || error);
      this.setState({ error: "no-audio" });
      return;
    }

    try {
      const activityEmitter = createActivityEmitter({
        noiseMeter: this.noiseMeter,
        durationToSleep: 2000,
        awakeMinimalVolume: 2 / 7
      });
      activityEmitter.on("activity-change", this.handleActivityChange);

      this.batteryStatus = await navigator.getBattery?.();
      if (this.batteryStatus) {
        this.batteryStatus.onchargingchange = this.handleBatteryChange;
        this.batteryStatus.onlevelchange = this.handleBatteryChange;
      }

      messenger.on(
        Message.DataRemoteSettings.name,
        this.handleDataRemoteSettingsMessage
      );
      messenger.on(
        Message.StopCommandMessage.name,
        this.handleStopCommandMessage
      );

      this.rpc = new RPC({ messenger });

      this.rpc.on(
        RPCRequests.StartMonitoring.name,
        this.handleStartMonitoringRPCRequest
      );
      this.rpc.on(RPCRequests.SyncFromPS.name, this.handleSyncFromPSRPCRequest);
      this.rpc.on(
        RPCRequests.StopMonitoring.name,
        this.handleStopMonitoringRPCRequest
      );
      this.rpc.on(
        RPCRequests.StartVideoBroadcasting.name,
        this.handleStartVideoBroadcastingRPCRequest
      );
      this.rpc.on(
        RPCRequests.StopVideoBroadcasting.name,
        this.handleStopVideoBroadcastingRPCRequest
      );
      this.rpc.on(
        RPCRequests.StartVideoReceiving.name,
        this.handleStartVideoReceivingRPCRequest
      );
      this.rpc.on(
        RPCRequests.StopVideoReceiving.name,
        this.handleStopVideoReceivingRPCRequest
      );
      this.rpc.on(RPCRequests.Ping.name, this.handlePingRPCRequest);
      electronOnly(() => {
        remote.powerMonitor.on("resume", this.onSystemResume);
        remote.powerMonitor.on("suspend", this.onSystemSuspend);
      });
      this.rpc.on(
        RPCRequests.PlayCommand.name,
        this.handlePlayCommandRPCRequest
      );
      this.rpc.on(
        RPCRequests.StartListening.name,
        this.handleStartListeningRPCRequest
      );
      this.rpc.on(
        RPCRequests.StopListening.name,
        this.handleStopListeningRPCRequest
      );
      this.rpc.on(
        RPCRequests.ChangeMonitoringMode.name,
        this.handleChangeMonitoringModeRPCRequest
      );
      this.rpc.on(
        RPCRequests.ChangeCamera.name,
        this.handleCameraChangeRequest
      );
      messenger.setPresence(Presence.SENDER, {
        avatar_id: defaultAvatarId
      });

      if (this.scheduler.isEnabled) this.scheduler.checkTasks();
      this.scheduler.on("task-start", (task) => {
        this.handleScheduledMonitoringStart(task);
      });
      this.scheduler.on("task-end", (task) => {
        this.handleScheduledMonitoringEnd(task);
      });
      this.scheduler.on("task-update", (task) => {
        this.handleSchedulerTaskUpdate(task);
      });
      this.scheduler.on("state-change", (task) => {
        this.handleSchedulerStateChange(task);
      });

      this.setState({
        hasCameraPermission,
        activityEmitter,
        noiseMeter: this.noiseMeter,
        localVideoStream: this.rtcManager.videoStream,
        status: "started",
        renderScheduledMonitoringDialog: this.scheduler.hasActiveTask ?? false
      });
      await this.recoverMonitoring();
    } catch (error) {
      log.error(error.message || error);
      this.props.router.navigate("/dashboard");
    }
  };
  componentDidUpdate = (prevProps, prevState) => {
    this.updatePresence(prevProps, prevState);
    this.appStatusChangeHandler(prevProps, prevState);
  };

  componentWillUnmount = () => {
    const { activityEmitter } = this.state;
    const { messenger } = this.props;
    this.unMounted = true;
    appDebugger.babyRtcManager = null;
    this.scheduler.stop();
    this.scheduler = null;
    this.motionDetection.stopMotionDetection();
    electronOnly(() => {
      remote.powerMonitor.off("resume", this.onSystemResume);
      remote.powerMonitor.off("suspend", this.onSystemSuspend);
    });
    messenger.off(
      Message.DataRemoteSettings.name,
      this.handleDataRemoteSettingsMessage
    );
    messenger.off(
      Message.StopCommandMessage.name,
      this.handleStopCommandMessage
    );
    this.rpc && this.rpc.destroy();
    if (this.rtcManager) {
      this.rtcManager.off("media-track", this.onTrack);
      this.rtcManager.destroy();
    }
    if (activityEmitter) {
      activityEmitter.off("activity-change", this.handleActivityChange);
      activityEmitter.destroy();
    }

    messenger.setPresence(Presence.NOT_CHOSEN_YET);
    this.deviceHandlerUnsubscribe?.();
    electronOnly(() => {
      this.powerSaveBlockerId &&
        remote.powerSaveBlocker.stop(this.powerSaveBlockerId);
      this.powerSaveBlockerId = null;
    });
  };

  onSystemSuspend = () => {
    this.rpc.off(RPCRequests.Ping.name, this.handlePingRPCRequest);
  };

  onSystemResume = () => {
    this.rpc.on(RPCRequests.Ping.name, this.handlePingRPCRequest);
  };

  /* Start, Stop */

  recoverMonitoring = async () => {
    const sessionsToRecover = monitoringRestorer.getRecoverableSessions();
    if (sessionsToRecover.length === 0) return;

    for (const session of sessionsToRecover) {
      const {
        monitoringId,
        jid,
        parentStationState,
        versionInfo,
        supportedFeatures
      } = session;
      this.startMonitoring({ monitoringId });
      this.setState(
        produce((prevState) => {
          prevState.parents[jid] = {
            versionInfo,
            supportedFeatures,
            jid,
            monitoringModeType: "FOREGROUND"
          };
        })
      );
      await this.rtcManager.initializeParent(jid, parentStationState);
    }
  };

  isFamilyDevice = (jid) => {
    const { pairedDevices } = this.props;
    return (
      Boolean(pairedDevices[jid]) && pairedDevices[jid].group === "myDevices"
    );
  };

  getLastNoiseOrMotion = (activityType) => {
    const { activity, activityChangeTime } = this.state;
    let elapsedTime = -1;
    const isFirstParent = this.getNumberOfParents() === 0;
    if (isFirstParent) {
      return elapsedTime;
    }

    if (
      activity === Activities.SLEEPING &&
      activityChangeTime?.[activityType]
    ) {
      elapsedTime = Date.now() - activityChangeTime[activityType];
    }
    return elapsedTime;
  };

  getMotionState = () => {
    const { motionSensitivity } = this.props;
    const { canDetectMotion } = this.state;
    return motionSensitivity === "MD_OFF"
      ? "NOT_RUNNING"
      : canDetectMotion
      ? "RUNNING"
      : "STOPPED_REASON_LOW_CAMERA_EXPOSURE";
  };

  getBabyStationState = ({ avatarId, motionSettings } = {}) => {
    const { micSensitivity, defaultAvatarId, motionSensitivity } = this.props;
    const { selectedCameraId } = this.state;
    return {
      lastNoise: this.getLastNoiseOrMotion(Activities.AWAKE),
      lastMotion: this.getLastNoiseOrMotion(Activities.MOTION),
      batteryStatus: createBatteryStatusPayload(this.batteryStatus),
      remoteSettings: {
        micSensitivity,
        avatar: { objectId: avatarId || defaultAvatarId },
        motionDetectionSensitivity:
          motionSettings?.motionSensitivity || motionSensitivity
      },
      cameraAndFlashlightState: { cameraId: selectedCameraId },
      motionDetectorState: motionSettings?.motionState || this.getMotionState()
    };
  };

  appStatusChangeHandler = (prevProps, prevState) => {
    const { parents } = this.state;
    const { appStatus, messenger } = this.props;
    if (
      prevProps.appStatus !== AppStatus.STARTED &&
      appStatus === AppStatus.STARTED
    ) {
      const message = Message.BabyStationState.create(
        this.getBabyStationState()
      );
      Object.values(parents).forEach((parent) => {
        messenger.sendAsEnvelope({ to: parent.jid, payload: { message } });
      });
    }
  };

  startMonitoring = ({ monitoringId } = { monitoringId: uuid4() }) => {
    let { monitoringStartTime } = this.state;
    const { messenger, defaultAvatarId } = this.props;

    if (!monitoringStartTime) {
      monitoringStartTime = Date.now();
      electronOnly(() => {
        this.powerSaveBlockerId = remote.powerSaveBlocker.start(
          "prevent-display-sleep"
        );
      });
    }

    if (!this.activityLogManager) {
      this.activityLogManager = new ActivityLogManager({
        rtcManager: this.rtcManager,
        noiseMeter: this.noiseMeter,
        messenger,
        monitoring: {
          monitoringId,
          startDate: new Date(monitoringStartTime),
          avatarId: defaultAvatarId
        },
        getParents: () => this.state.parents,
        updateParent: this.updateParent,
        rpc: this.rpc
      });
      this.mediaRecorder = this.activityLogManager.mediaRecorder;
      this.activityLogPushNotificator = this.activityLogManager.pushNotificator;
      this.rtcManager.on(
        "local-audio-data",
        this.activityLogManager.onAudioData
      );
      this.motionDetection.monitoringStarted();
    }

    this.setState(
      produce((prevState) => {
        if (!prevState.monitoringId) {
          prevState.monitoringId = monitoringId;
        }
        if (!prevState.monitoringStartTime) {
          prevState.monitoringStartTime = monitoringStartTime;
        }
      })
    );
  };

  handleStartMonitoringRPCRequest = async (request, from, sendResponse) => {
    let { monitoringStartTime, monitoringId, cameraList, selectedCameraId } =
      this.state;
    const { pairedDevices } = this.props;
    let { defaultAvatarId } = this.props;

    if (!monitoringId) monitoringId = uuid4();

    if (pairedDevices[from] == null) return;
    if (this.state.parents[from]) this.rtcManager.removeParent(from);
    this.startMonitoring({ monitoringId });

    const { versionInfo, supportedFeatures, avatar, parentStationState } =
      request;

    monitoringRestorer.setRecoverableSessions({
      monitoringId,
      jid: from,
      ...request
    });

    if (
      this.isFamilyDevice(from) &&
      avatar &&
      avatar.objectId !== defaultAvatarId
    ) {
      this.handleAvatarChange(avatar.objectId, { skipJid: from });
      defaultAvatarId = avatar.objectId;
    }

    let monitoringTime;
    if (!monitoringStartTime) monitoringTime = 0;
    else monitoringTime = Date.now() - monitoringStartTime;

    const response = RPCResponses.StartMonitoring.create({
      monitoringTime,
      monitoringId,
      senderStationConfiguration: {
        numberOfCameras: cameraList?.length || 0,
        availableCameras: cameraList || [],
        selectedCameraId
      },
      babyStationState: this.getBabyStationState({
        avatarId: defaultAvatarId
      }),
      resultCode: "SRPC_OK",
      unlimitedTimeMonitoring: true
    });
    sendResponse(response);

    if (this.state.parents[from]?.monitoringModeType === "BACKGROUND_OFFLINE")
      this.activityLogPushNotificator.parentLeftBackgroundMode(from);

    this.setState(
      produce((prevState) => {
        prevState.parents[from] = {
          versionInfo,
          supportedFeatures,
          jid: from,
          monitoringModeType: "FOREGROUND"
        };
      })
    );
    this.rtcManager.initializeParent(from, {
      videoQualitySettings: parentStationState?.videoQualitySettings,
      networkType: parentStationState?.networkType
    });
  };

  handleSyncFromPSRPCRequest = async (
    { parentStationState },
    from,
    sendResponse
  ) => {
    debug.babyStation(
      "Received SyncFromPSRPCRequest, parentStationState:",
      parentStationState
    );
    if (!this.state.parents[from]) return;
    const { monitoringModeType, videoQualitySettings, networkType } =
      parentStationState;
    this.rtcManager.updatePeer(from, { videoQualitySettings, networkType });
    this.handleMonitoringModeTypeChange({
      monitoringModeType,
      parentJid: from
    });

    const response = RPCResponses.SyncFromBS.create({
      babyStationState: this.getBabyStationState()
    });
    sendResponse(response);
  };

  handleStopMonitoringRPCRequest = async (request, from, sendResponse) => {
    const { parents } = this.state;
    const continueMonitoring = this.scheduler.hasActiveTask;

    const parent = parents[from];
    if (parent == null) return;

    const isInBackgroundMode = parent.monitoringModeType !== "FOREGROUND";
    if (this.activityLogPushNotificator && isInBackgroundMode)
      this.activityLogPushNotificator.parentLeftBackgroundMode(from);

    const isLastParent = this.getNumberOfParents() === 1;
    if (isLastParent && !continueMonitoring) this.stopMonitoring();

    this.rtcManager.removeParent(from);
    this.setState(
      produce((prevState) => {
        delete prevState.parents[from];
        if (prevState.videoReceivingFrom === from) {
          prevState.videoReceivingFrom = null;
          prevState.remoteVideoStream = null;
        }
      })
    );

    const response = RPCResponses.StopMonitoring.create({
      allowsNewConnection: true
    });
    sendResponse(response);
  };

  stopMonitoring = () => {
    const { monitoringId } = this.state;
    if (this.scheduler.hasActiveTask) {
      this.scheduler.cancelActiveTasks();
      this.scheduler.sendStopNotification(monitoringId);
    }
    monitoringRestorer.removeRecoverableSession();
    this.setState({ status: "stopping" });

    this.pushNotificator.monitoringStopped();
    this.activityLogManager.monitoringEnded();
    this.motionDetection.monitoringEnded();

    this.rtcManager.off(
      "local-audio-data",
      this.activityLogManager.onAudioData
    );

    this.activityLogManager = null;
    this.mediaRecorder = null;
    this.activityLogPushNotificator = null;

    electronOnly(() => {
      this.powerSaveBlockerId &&
        remote.powerSaveBlocker.stop(this.powerSaveBlockerId);
      this.powerSaveBlockerId = null;
    });

    this.setState(
      produce((prevState) => {
        prevState.activityChangeTime = null;
        prevState.monitoringStartTime = null;
        prevState.monitoringId = null;
      })
    );
  };

  stopBroadcasting = async (reason = "STOP_REASON_USER_STOP") => {
    this.stopMonitoring();
    const { parents } = this.state;

    const parentsJids = Object.keys(parents);
    const request = RPCRequests.StopMonitoringFromBaby.create({
      reason
    });
    for (let jid of parentsJids) {
      try {
        this.rpc.call(request, jid);
        this.rtcManager.removeParent(jid);
      } catch (error) {
        log.error(error);
      }
    }

    this.setState(
      produce((prevState) => {
        prevState.parents = {};
        prevState.videoReceivingFrom = null;
        prevState.remoteVideoStream = null;
      })
    );
  };

  handleMonitoringModeTypeChange = async ({
    monitoringModeType,
    parentJid
  }) => {
    const { parents, activity } = this.state;
    const parent = parents[parentJid];
    if (parent == null) return;
    if (parent.monitoringModeType === monitoringModeType) return;

    switch (monitoringModeType) {
      case "BACKGROUND_ONLINE_PIP":
      case "BACKGROUND_ONLINE":
        this.activityLogPushNotificator.parentEnteredBackgroundMode(
          parent,
          activity
        );
        this.updateParent(parentJid, { monitoringModeType });
        if (monitoringModeType === "BACKGROUND_ONLINE") {
          try {
            await this.rtcManager.stopSendingVideoTo(parentJid);
          } catch (error) {
            log.error(error);
          }
        }
        break;

      case "FOREGROUND": {
        this.activityLogPushNotificator.parentLeftBackgroundMode(parentJid);
        this.updateParent(parentJid, { monitoringModeType: "FOREGROUND" });
        const peer = this.rtcManager.getPeer(parentJid);
        if (peer == null) {
          this.rtcManager.initializeParent(parentJid);
        }
        break;
      }

      case "BACKGROUND_OFFLINE":
      default: {
        this.rtcManager.removeParent(parentJid);
        debug.babyStation(
          `Removed parent who entered offline mode from RtcManager`
        );
        this.updateParent(parentJid, {
          monitoringModeType: "BACKGROUND_OFFLINE"
        });
        this.activityLogPushNotificator.parentEnteredBackgroundMode(
          parent,
          activity
        );
      }
    }
  };

  handleChangeMonitoringModeRPCRequest = async (
    request,
    from,
    sendResponse
  ) => {
    debug.babyStation(
      `Received ChangeMonitoringModeRPCRequest monitoringModeType: ${request.monitoringModeType} from ${from}`
    );
    if (!this.state.parents[from]) return;
    this.handleMonitoringModeTypeChange({
      monitoringModeType: request.monitoringModeType,
      parentJid: from
    });

    sendResponse(null, "xmpp");
  };

  /* Start, Stop - END */

  /* Video Mode */
  handleStartVideoBroadcastingRPCRequest = async (
    request,
    from,
    sendResponse
  ) => {
    const { selectedCameraId } = this.state;
    let result;
    if (this.rtcManager.hasCamera) {
      try {
        await this.rtcManager.startSendingVideoTo(from, selectedCameraId);
        result = "SUCCESS";
      } catch (error) {
        debug.babyStation(error);
        result = "NOT_AVAILABLE";
      }
    } else result = "NOT_AVAILABLE";
    const response = RPCResponses.StartVideoBroadcasting.create({
      result,
      cameraId: selectedCameraId
    });
    sendResponse(response);
  };
  handleStopVideoBroadcastingRPCRequest = async (
    request,
    from,
    sendResponse
  ) => {
    try {
      await this.rtcManager.stopSendingVideoTo(from);
      if (this.state.videoReceivingFrom === from)
        this.setState({ videoReceivingFrom: null });
    } catch (error) {
      log.error(error);
    }
    sendResponse();
  };
  /* Video Mode - END */

  /* See Me  */
  handleStartVideoReceivingRPCRequest = (request, from, sendResponse) => {
    const { videoReceivingFrom } = this.state;
    const isValidRequest =
      videoReceivingFrom === null || videoReceivingFrom === from;
    const { frame } = this.props;

    isValidRequest && this.setState({ videoReceivingFrom: from });
    const response = RPCResponses.StartVideoReceiving.create({
      result: isValidRequest ? "SUCCESS" : "ALREADY_RUNNING",
      previewAspectRatio:
        window.innerWidth / (window.innerHeight - (frame ? frame.height : 0))
    });
    sendResponse(response);
  };

  handleStopVideoReceivingRPCRequest = (request, from, sendResponse) => {
    this.setState({ videoReceivingFrom: null });
    sendResponse();
  };

  stopVideoReceiving = () => {
    const { videoReceivingFrom } = this.state;
    const { messenger } = this.props;
    const message = Message.StopVideoBroadcasting.create();
    messenger.sendAsEnvelope({ to: videoReceivingFrom, payload: { message } });
    this.setState({ videoReceivingFrom: null });
  };
  onTrack = (e, parentJid) => {
    const { videoReceivingFrom } = this.state;
    if (e.track.kind !== "video") return;
    const remoteVideoStream = e.streams[0];
    if (parentJid === videoReceivingFrom) {
      this.setState({ remoteVideoStream });
    }
  };
  /* See Me END */

  handleActivityChange = (activity) => {
    this.setState(({ activityChangeTime }) => ({
      activity,
      activityChangeTime: { ...activityChangeTime, [activity]: Date.now() }
    }));
    this.activityLogPushNotificator &&
      this.activityLogPushNotificator.onActivityChange(activity);

    this.rtcManager.activity = activity;
  };

  handleMotionIndication = (indicate) => {
    if (this.state.indicateMotion === indicate) return;
    this.setState({ indicateMotion: indicate });
  };

  /* COMMANDS */
  handlePlayCommandRPCRequest = (request, from, sendResponse) => {
    const { commandId } = request;
    const { commands } = this.props;
    let commandPlayer = this.commandPlayers[from];

    if (commandPlayer) commandPlayer.pause();
    else commandPlayer = this.commandPlayers[from] = new Audio();

    if (commands[commandId]) {
      if (environment.isBrowser) {
        commandPlayer.src = commands[commandId].audioFile.url;
      } else {
        commandPlayer.src = commandFiles.getSrc(
          commands[commandId].audioFile.name
        );
      }
      commandPlayer.onerror = async () => {
        const response = await fetch(commandPlayer.src);
        const arrayBuffer = await response.arrayBuffer();
        decodeOgg(commandPlayer, arrayBuffer);
      };
      commandPlayer.play();
      const response = RPCResponses.PlayCommand.create({
        commandId,
        currentTime: Math.round(commandPlayer.currentTime * 1000),
        playing: true
      });
      sendResponse(response);
    }
  };
  handleStopCommandMessage = (payload, from) => {
    const commandPlayer = this.commandPlayers[from];
    if (commandPlayer) {
      commandPlayer.pause();
    }
  };
  /* COMMANDS - END */

  /* REMOTE SETTINGS */
  handleDataRemoteSettingsMessage = (remoteSettings, from) => {
    const { parents } = this.state;
    const { defaultAvatarId, micSensitivity, motionSensitivity } = this.props;

    if (parents[from] == null) return;

    const {
      avatar,
      micSensitivity: newMicSensitivity,
      motionDetectionSensitivity: newMotionSensitivity
    } = remoteSettings;

    if (avatar && avatar.objectId !== defaultAvatarId) {
      this.handleAvatarChange(avatar.objectId, { shouldAnnounceChange: false });
    }

    if (micSensitivity !== newMicSensitivity)
      this.handleMicSensitivityChange(newMicSensitivity, {
        shouldAnnounceChange: false
      });

    if (motionSensitivity !== newMotionSensitivity) {
      this.motionDetection.handleMotionSensitivityChange(newMotionSensitivity);
    }

    this.announceSettingsChange(remoteSettings, from);
  };
  announceSettingsChange = (remoteSettings, skipJid) => {
    const { parents } = this.state;
    const { messenger, defaultAvatarId, micSensitivity, motionSensitivity } =
      this.props;

    const message = Message.DataRemoteSettings.create({
      avatar: remoteSettings.avatar || { objectId: defaultAvatarId },
      micSensitivity: remoteSettings.micSensitivity || micSensitivity,
      motionDetectionSensitivity:
        remoteSettings.motionDetectionSensitivity || motionSensitivity
    });

    Object.values(parents).forEach((parent) => {
      if (
        parent.jid === skipJid ||
        parent.monitoringModeType === "BACKGROUND_OFFLINE"
      )
        return;
      messenger.sendAsEnvelope({ to: parent.jid, payload: { message } });
    });
  };
  handleAvatarChange = async (
    avatarId,
    { shouldAnnounceChange = true, skipJid } = {}
  ) => {
    const { setDefaultAvatar, avatars, synchronizeAvatars } = this.props;
    if (avatars[avatarId] == null) {
      debug.babyStation("Synchronizing Avatars Manually");
      await synchronizeAvatars();
    }

    setDefaultAvatar(avatarId);
    if (!this.activityLogManager) return;
    this.activityLogManager.updateCurrentMonitoring({ avatarId });
    if (shouldAnnounceChange) {
      const remoteSettings = { avatar: { objectId: avatarId } };
      this.announceSettingsChange(remoteSettings, skipJid);
    }
  };

  getNextCameraId = () => {
    const { cameraList, selectedCameraId } = this.state;
    let cameraIndex;
    cameraList.forEach((camera) => {
      if (camera.deviceId === selectedCameraId)
        cameraIndex = cameraList.indexOf(camera);
    });
    return cameraList[cameraIndex + 1]
      ? cameraList[cameraIndex + 1].deviceId
      : cameraList[0].deviceId;
  };

  handleCameraChange = async (requestedCameraId, skipJid) => {
    let cameraId = requestedCameraId;
    if (!cameraId || cameraId === "") {
      cameraId = this.getNextCameraId();
    }
    const { parents } = this.state;
    const { messenger } = this.props;
    const numberOfParents = Object.keys(parents).length;
    try {
      await this.rtcManager.changeCamera({ cameraId });
    } catch (err) {
      this.stopBroadcasting();
      throw new Error("Failed to update video stream");
    }
    this.setState({ selectedCameraId: cameraId });
    if (numberOfParents === 0) return;
    this.mediaRecorder.updateMediaRecorder(cameraId);
    const message = Message.CameraAndFlashlightState.create({ cameraId });
    Object.values(parents).forEach((parent) => {
      if (parent.monitoringModeType === "BACKGROUND_OFFLINE") return;
      if (parent.jid === skipJid) return;
      messenger.sendAsEnvelope({ to: parent.jid, payload: { message } });
    });
  };

  handleCameraChangeRequest = async (request, from, sendResponse) => {
    const { cameraId } = request;
    let result;
    try {
      await this.handleCameraChange(cameraId, from);
      result = "SUCCESS";
    } catch (err) {
      result = "FAILURE";
    }
    const response = RPCResponses.ChangeCamera.create({
      cameraId,
      result
    });
    sendResponse(response);
  };

  handleMicSensitivityChange = (
    micSensitivity,
    { shouldAnnounceChange = true } = {}
  ) => {
    const { setMicSensitivity, micSensitivityCorrection } = this.props;
    setMicSensitivity(micSensitivity);
    this.noiseMeter.setSensitivity(
      MicSensitivity[micSensitivity].value,
      micSensitivityCorrection
    );

    if (shouldAnnounceChange) {
      this.announceSettingsChange({ micSensitivity });
    }
  };

  changeMotionDetectionSensitivity = (motionSensitivity) => {
    const { parents } = this.state;
    const { defaultAvatarId, micSensitivity } = this.props;
    this.motionDetection.handleMotionSensitivityChange(motionSensitivity);
    if (Object.keys(parents).length === 0 || !parents) return;

    const remoteSettings = {
      avatar: { objectId: defaultAvatarId },
      micSensitivity: micSensitivity,
      motionDetectionSensitivity: motionSensitivity
    };
    this.announceSettingsChange(remoteSettings);
  };
  /* REMOTE SETTINGS - END */

  handleDetectionAvailabilityChange = ({ canDetectMotion }) => {
    if (this.state.canDetectMotion === canDetectMotion) return;
    this.setState({ canDetectMotion });
    this.notifyAboutMotionAvailabilityChange({
      motionState: canDetectMotion
        ? "RUNNING"
        : "STOPPED_REASON_LOW_CAMERA_EXPOSURE"
    });
  };

  notifyAboutMotionAvailabilityChange = (motionSettings = {}) => {
    const { parents } = this.state;
    const { messenger } = this.props;
    if (Object.keys(parents).length === 0 || !parents) return;

    const message = Message.BabyStationState.create(
      this.getBabyStationState({ motionSettings })
    );
    Object.values(parents).forEach((parent) => {
      messenger.sendAsEnvelope({ to: parent.jid, payload: { message } });
    });
  };

  /* Listening - Every Noise */
  handleStartListeningRPCRequest = (request, from, sendResponse) => {
    this.rtcManager.peersWithEveryNoise[from] = true;
    sendResponse();
  };
  handleStopListeningRPCRequest = (request, from, sendResponse) => {
    delete this.rtcManager.peersWithEveryNoise[from];
    sendResponse();
  };

  /* Listening - Every Noise - END */

  updatePresence(prevProps, prevState) {
    const { parents, monitoringId } = this.state;
    const { defaultAvatarId, messenger, presence } = this.props;

    const numberOfParents = Object.keys(parents).length;
    const newPresence =
      numberOfParents > 0 ? Presence.SENDER_CONNECTED : Presence.SENDER;

    if (
      newPresence === presence &&
      defaultAvatarId === prevProps.defaultAvatarId
    )
      return;

    debug.rtcManager("updating presence to", newPresence);

    messenger.setPresence(newPresence, {
      avatar_id: defaultAvatarId,
      monitoring_id: monitoringId || undefined
    });
  }
  handlePingRPCRequest = (request, from, sendResponse) => {
    const { pairedDevices } = this.props;
    if (!pairedDevices[from]) return;

    const response = RPCResponses.Ping.create();
    sendResponse(response);
  };
  handleBatteryChange = () => {
    const { parents } = this.state;
    const { messenger } = this.props;

    const payload = createBatteryStatusPayload(this.batteryStatus);

    if (payload.level === LOW_BATTERY_NOTIFICATION_LEVEL) {
      debug.babyStation("Battery dropped below notification level");
      this.pushNotificator.lowBattery();
    }

    const message = Message.BatteryStatus.create(payload);
    Object.values(parents).forEach((parent) => {
      if (parent.monitoringModeType === "BACKGROUND_OFFLINE") return;
      messenger.sendAsEnvelope({ to: parent.jid, payload: { message } });
    });
  };

  handleScheduledMonitoringStart = (task) => {
    const { monitoringId } = this.state;
    const isMonitoring = Boolean(monitoringId);

    if (task && !isMonitoring) {
      debug.babyStation("Starting monitoring for task: ", task);
      this.startMonitoring();
      this.scheduler.sendStartNotification(monitoringId);
      this.activityLogPushNotificator.startedScheduledMonitoring();
    }
  };

  handleScheduledMonitoringEnd = (task) => {
    const { monitoringId, parents } = this.state;
    const isMonitoring = Boolean(monitoringId);
    const hasParents = Object.keys(parents).length !== 0;

    if (task && isMonitoring) {
      debug.babyStation("Ending monitoring for task: ", task);
      this.activityLogPushNotificator.stoppedScheduledMonitoring();

      if (!hasParents) {
        this.scheduler.cancelActiveTasks();
        this.scheduler.sendStopNotification(monitoringId);
        this.stopMonitoring();
      } else this.stopBroadcasting("STOP_REASON_SCHEDULER_STOP");
    }
  };

  handleSchedulerTaskUpdate = (task) => {
    const { monitoringId, parents } = this.state;

    const isMonitoring = Boolean(monitoringId);
    const hasParents = Object.keys(parents).length !== 0;

    if (isMonitoring && !hasParents && !this.scheduler.hasActiveTask) {
      debug.babyStation("Ending monitoring for task: ", task);
      this.scheduler.sendStopNotification(monitoringId);
      this.stopMonitoring();
    }
  };

  handleSchedulerStateChange = (state) => {
    const { monitoringId, parents } = this.state;

    const isMonitoring = Boolean(monitoringId);
    const hasParents = Object.keys(parents).length !== 0;

    if (state === "inactive" && !hasParents && isMonitoring) {
      debug.babyStation("Ending scheduled monitoring");
      this.scheduler.sendStopNotification(monitoringId);
      this.stopMonitoring();
    }
  };

  cancelScheduledMonitoring = () => {
    this.scheduler.cancelActiveTasks();
    this.setState({ renderScheduledMonitoringDialog: false });
  };

  startScheduledMonitoring = () => {
    this.setState({ renderScheduledMonitoringDialog: false });
    this.handleScheduledMonitoringStart(this.scheduler?.activeTasks[0]);
  };

  openStartScheduledMonitoringDialog = () => {
    this.setState({ renderScheduledMonitoringDialog: true });
  };

  render() {
    const {
      localVideoStream,
      status,
      error,
      remoteVideoStream,
      monitoringStartTime,
      hasCameraPermission,
      cameraList,
      selectedCameraId,
      activity,
      indicateMotion,
      canDetectMotion,
      renderStopMonitoringDialog,
      monitoringId,
      renderScheduledMonitoringDialog,
      videoReceivingFrom
    } = this.state;
    const { presence, motionSensitivity, router } = this.props;

    return (
      <BabyStationContext.Provider value={this.state}>
        <NoAudioDialog
          isOpen={error === "no-audio"}
          onDismiss={() => router.navigate("/dashboard")}
        />

        <StartScheduledMonitoringDialog
          isOpen={renderScheduledMonitoringDialog}
          onOk={this.startScheduledMonitoring}
          onDismiss={this.cancelScheduledMonitoring}
        />

        <StopMonitoringDialog
          isOpen={renderStopMonitoringDialog}
          onDismiss={() => this.setState({ renderStopMonitoringDialog: false })}
          onOk={() => {
            this.setState({ renderStopMonitoringDialog: false });
            if (presence === Presence.SENDER_CONNECTED) {
              this.stopBroadcasting();
            } else {
              this.stopMonitoring();
            }
          }}
        />

        <Monitor
          isMonitoring={Boolean(monitoringId)}
          status={status}
          localVideoStream={localVideoStream}
          remoteVideoStream={videoReceivingFrom ? remoteVideoStream : null}
          presence={presence}
          noiseMeter={this.noiseMeter}
          monitoringStartTime={monitoringStartTime}
          hasCameraPermission={hasCameraPermission}
          stopBroadcasting={() =>
            this.setState({ renderStopMonitoringDialog: true })
          }
          onAvatarChange={this.handleAvatarChange}
          handleCameraChange={this.handleCameraChange}
          stopVideoReceiving={this.stopVideoReceiving}
          hasCamera={status === "starting" ? false : this.rtcManager.hasCamera}
          selectedCameraId={selectedCameraId}
          cameraList={cameraList}
          activity={activity}
          motionSensitivity={motionSensitivity}
          canDetectMotion={canDetectMotion}
          indicateMotion={indicateMotion}
          scheduler={this.scheduler}
          openStartScheduledMonitoringDialog={
            this.openStartScheduledMonitoringDialog
          }
        />
      </BabyStationContext.Provider>
    );
  }
}
